import { collection, deleteField, doc, getDocsFromServer, onSnapshot, query, updateDoc, where } from 'firebase/firestore';
import { FUNCOES } from '@/equipe/funcoes';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { tratarErro, tratarErroDeCallable } from '@/lib/erros';
import { ouvirVigiado } from '@/lib/listenerVigiado';

/**
 * ★★★ WT lote 1 — TODA CALLABLE PASSA POR AQUI.
 *
 * ⚠⚠ Erro de callable chega ao cliente com o `code` do servidor (`permission-denied`,
 * `failed-precondition`, `resource-exhausted`) — e é **o diagnóstico mais valioso que
 * existe**: ele diz que a tela pediu algo que o servidor recusou. ★ Até agora ele só
 * aparecia na mensagem que a pessoa lia e esquecia.
 *
 * ★ O embrulho **registra e relança**: quem chama continua tratando o erro como antes
 * (as telas leem `e.code` para escolher a mensagem). ⚠ Engolir aqui quebraria todas elas.
 */
const chamarCallable = (nome) => async (dados) => {
  try {
    return await httpsCallable(functions, nome)(dados);
  } catch (erro) {
    throw tratarErroDeCallable(nome, erro);
  }
};


const porNome = (a, b) => (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR');

const equipeDoSnap = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(porNome);

/**
 * Equipe da clínica. Usado nos filtros por SDR e na atribuição de leads.
 *
 * ★★★ WU-2/P3+P7 — É A LISTA DO CASO DE 03/out (*"Fulano (fora da função)"*), e são **9
 * documentos**: ⚠ o cache não economiza nada aqui e pode custar cinco dias de trabalho
 * atrapalhado. ★ A 1ª emissão vinda do cache passa a ser conferida com o servidor.
 *
 * ⚠ O sinal do U3 (`equipeIncoerente`) continua onde está, no provider: ★ ele cobre o caso em
 * que a lista chega **errada** sem erro nenhum, e isto cobre o caso em que ela chega **velha**.
 */
export function ouvirEquipe(clinicaId, aoReceber, aoFalhar, aoDesatualizar) {
  const q = query(collection(db, 'clinicas', clinicaId, 'usuarios'), where('ativo', '==', true));
  return ouvirVigiado({
    assunto: 'a equipe',
    assinar: (aoSnap, aoErro) => onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => aoSnap(equipeDoSnap(snap), snap.metadata.fromCache),
      aoErro,
    ),
    doServidor: async () => equipeDoSnap(await getDocsFromServer(q)),
    aoReceber,
    aoFalhar,
    aoDesatualizar,
  });
}

/**
 * TODOS os usuários da clínica, ativos e inativos — a tela de Equipe.
 *
 * Separado do `ouvirEquipe` de propósito, e não com um parâmetro: aquele filtra
 * `ativo == true` porque quem atribui lead ou transfere conversa não pode nem
 * ver quem saiu da clínica. Aqui é o contrário — sem os inativos não haveria
 * como reativar ninguém, e a lista mentiria sobre quem tem acesso.
 */
export function ouvirUsuariosDaClinica(clinicaId, aoReceber, aoFalhar) {
  return onSnapshot(
    collection(db, 'clinicas', clinicaId, 'usuarios'),
    (snap) => aoReceber(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(porNome)),
    tratarErro('os usuários da clínica', aoFalhar),
  );
}

// ---------------------------------------------------------------- escritas
// Papel e `ativo` NÃO são escritos pelo cliente: as rules os bloqueiam, porque
// mexer neles exige também trocar as custom claims e desabilitar a conta no
// Auth — coisas que só o Admin SDK faz. Daí as callables.

export const criarUsuario = (dados) => chamarCallable('criarUsuario')(dados);

/**
 * ★ VP — FUNÇÃO (sdr/negociador) NÃO é papel: não mexe em claim, então vai
 * direto no doc da equipe. As rules só deixam o gestor gravar, e só lista com
 * valores em FUNCOES. A lista explícita vence o default derivado — inclusive [].
 */
export function definirFuncoes(clinicaId, uid, funcoes) {
  const validas = FUNCOES.filter((f) => (funcoes ?? []).includes(f));
  return updateDoc(doc(db, 'clinicas', clinicaId, 'usuarios', uid), { funcoes: validas });
}
/**
 * ★ WA — quem entra no RODÍZIO de leads. Marca explícita por pessoa: ausente = FORA.
 *
 * Não passa pela callable (como `definirFuncoes`): não é claim, e as rules já exigem gestor para escrever
 * em `usuarios` o que não seja o próprio perfil. Precisa ser explícita porque "quem tem a função sdr" inclui
 * o GESTOR (a função é derivada do papel quando `funcoes` não existe) — e metade dos leads cairia no dono.
 */
export function definirNoRodizio(clinicaId, uid, valor) {
  return updateDoc(doc(db, 'clinicas', clinicaId, 'usuarios', uid), { noRodizio: Boolean(valor) });
}

export const atualizarUsuario = (dados) => chamarCallable('atualizarUsuario')(dados);

/**
 * O gestor define uma senha temporária para um colaborador.
 *
 * A senha vai no corpo da chamada e **não volta na resposta** — quem chamou
 * acabou de digitá-la, e ecoá-la só criaria mais um lugar de onde ela vaza.
 */
export const redefinirSenha = (dados) => chamarCallable('redefinirSenha')(dados);

/** Traduz os códigos das callables para algo que o gestor entenda. */
export const MENSAGENS_EQUIPE = {
  'functions/already-exists': 'Já existe uma conta com este e-mail.',
  'functions/invalid-argument':
    'Confira os dados: nome, e-mail válido, senha com ao menos 8 caracteres e papel.',
  'functions/permission-denied': 'Apenas o gestor da clínica pode administrar a equipe.',
  'functions/failed-precondition': 'Você não pode desativar a própria conta.',
  'functions/not-found': 'Usuário não encontrado nesta clínica.',
  'functions/unauthenticated': 'Sua sessão expirou. Entre de novo.',
  'functions/unavailable': 'Não foi possível falar com o servidor. Tente de novo.',
};

export const mensagemDoErro = (e, padrao = 'Não foi possível concluir a operação.') =>
  MENSAGENS_EQUIPE[e?.code] ?? e?.message ?? padrao;

/**
 * ★★★ A-2 — O HORÁRIO DE TRABALHO DE UMA PESSOA (`usuarios/{uid}.expediente`).
 *
 * Mesmo caminho de `definirFuncoes` e `definirNoRodizio`: escrita direta, sem callable —
 * ★ não é claim, e as rules exigem gestor para escrever em `usuarios` o que não seja o próprio
 * perfil. ⚠⚠ E desde esta peça `expediente` está na lista que **a própria pessoa não pode
 * editar**, junto de `papel`, `ativo`, `metas` e `funcoes`: ★ horário é ordem de serviço, não
 * preferência — quem o mudasse mudaria a própria meta e o próprio ritmo.
 *
 * ★★ `null` apaga o campo e a pessoa **volta a seguir o horário da clínica** — ⚠ é o que o botão
 * "Seguir o horário da clínica" faz. `deleteField()` em vez de `{}`: ★ objeto vazio sobreviveria
 * ao `dia in salvo` de `expedienteDaPessoa` e deixaria a pessoa sem dia nenhum.
 */
export function definirExpediente(clinicaId, uid, expediente) {
  return updateDoc(doc(db, 'clinicas', clinicaId, 'usuarios', uid), {
    expediente: expediente === null ? deleteField() : expediente,
  });
}
